import { ref } from "vue"
import { Api } from "@/services/ApiService"
import { DISTRIBUTION } from "@/config/distribution"

/** A workspace's brand, as `GET tenant/{uuid}/branding` and the user payload's `branding` give it. */
export interface WorkspaceBranding {
    name: string
    color: string | null
    logo: string | null
}

const STORAGE_KEY = "signed_out_branding"

function readStored(): WorkspaceBranding | null {
    try {
        const value = localStorage.getItem(STORAGE_KEY)
        return value ? (JSON.parse(value) as WorkspaceBranding) : null
    } catch {
        return null
    }
}

/**
 * The workspace brand the signed-out screens wear (COACHIO-1942). Only the general
 * app follows a workspace; a white-label build keeps its compiled brand. Kept in
 * localStorage so a client who comes back without the link still sees it.
 */
export const signedOutBranding = ref<WorkspaceBranding | null>(
    DISTRIBUTION.allowTenantColor ? readStored() : null,
)

/**
 * The invitation's confirmation page links to `/login?tenant=<uuid>`: fetch that
 * workspace's brand and remember it. When it cannot be read, the default brand is
 * shown rather than an earlier workspace's next to this workspace's link.
 */
export async function loadSignedOutBranding(search: string): Promise<void> {
    const uuid = new URLSearchParams(search).get("tenant")
    if (!uuid || !DISTRIBUTION.allowTenantColor) return

    const result = await Api.get<{ data: WorkspaceBranding }>(`tenant/${encodeURIComponent(uuid)}/branding`)
    rememberSignedOutBranding(result.ok ? result.data?.data ?? null : null)
}

/**
 * Remember a workspace brand for the signed-out screens. Also called with the
 * signed-in user's own branding, so logging out shows their workspace and not
 * one from an invitation link opened earlier.
 */
export function rememberSignedOutBranding(branding: WorkspaceBranding | null): void {
    if (!DISTRIBUTION.allowTenantColor) return

    signedOutBranding.value = branding
    try {
        if (branding) localStorage.setItem(STORAGE_KEY, JSON.stringify(branding))
        else localStorage.removeItem(STORAGE_KEY)
    } catch {
        // Private mode: the brand still applies for this visit.
    }
}

/** Signed in: the user's workspace color. Signed out: the remembered workspace's. */
export function appBrandColor(authenticated: boolean, userColor: string | null | undefined): string {
    const color = authenticated ? userColor : signedOutBranding.value?.color
    return DISTRIBUTION.allowTenantColor && color ? color : DISTRIBUTION.primaryColor
}
